import {
  type OutletResource,
  OutletsController_list_v1,
  type RevenueShareResource,
  RevenueSharesController_listAt_v1,
} from '@repo/api-client';
import { Alert, AlertDescription } from '@repo/ui/components/alert';
import { Button } from '@repo/ui/components/button';
import { Label } from '@repo/ui/components/label';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@repo/ui/components/select';
import { TableBody, TableCell, TableHead, TableHeader, TableRow } from '@repo/ui/components/table';
import { IconCircleCheck, IconPlus } from '@tabler/icons-react';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { useApiClient } from '@/api/api-client';
import { collectPages } from '@/api/collect-pages';
import { isForbidden } from '@/api/failure';
import { useSession } from '@/auth/session';

import {
  EmptyState,
  LoadFailed,
  Screen,
  ScreenFact,
  ScreenTable,
  TableNotice,
  TableSkeleton,
} from '../back-office/Screen';
import { formatBusinessDate } from '../tickets/business-date';
import { CloseAgreementDialog, OpenAgreementDialog } from './AgreementDialogs';
import { formatRate } from './rate';

/**
 * Partners' percentages of an outlet's full collection (#258):
 * `GET /v1/outlets/{outletId}/revenue-shares`, owner and manager.
 *
 * Part 1 of the daily collection sheet. A partner is paid a percentage of the
 * outlet's **whole** day's takings, not a net figure, at the rate in force that
 * day — so an agreement is never edited: it is closed, and the next is opened.
 *
 * **The owner writes; a manager reads.** Opening and closing are
 * `@RequireRole('OWNER')` on the API, so both are hidden from a manager. That
 * is courtesy — the role is editable in `sessionStorage` — and each dialog
 * still maps `AUTH_FORBIDDEN` if it arrives.
 *
 * **Read whole, and deliberately.** The set grows only when a partner's rate
 * changes, a few rows a year per outlet, and the overlap refusal names an
 * agreement by id — the open dialog has to find it among these to offer to
 * close it, which a single page could not promise.
 */

type ListState =
  | { readonly kind: 'loading' }
  | { readonly kind: 'loaded'; readonly agreements: readonly RevenueShareResource[] }
  | { readonly kind: 'failed'; readonly error: unknown };

type Opened =
  | { readonly kind: 'open' }
  | { readonly kind: 'close'; readonly agreement: RevenueShareResource };

export function RevenueShares() {
  const client = useApiClient();
  const { session } = useSession();

  // Courtesy, not security: see the note above.
  const mayWrite = session?.user.role === 'OWNER';

  const [outlets, setOutlets] = useState<readonly OutletResource[]>([]);
  const [outletId, setOutletId] = useState('');
  const [state, setState] = useState<ListState>({ kind: 'loading' });
  const [opened, setOpened] = useState<Opened | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const [reloads, setReloads] = useState(0);
  const reload = useCallback(() => setReloads((count) => count + 1), []);

  useEffect(() => {
    const controller = new AbortController();
    let live = true;

    // Trading outlets only: the API refuses to open an agreement at a retired
    // one, and the park's outlets are few enough to read whole.
    collectPages<OutletResource>((offset, limit) =>
      client.request(OutletsController_list_v1, {
        query: { offset, limit },
        signal: controller.signal,
      }),
    )
      .then((all) => {
        if (!live) return;
        setOutlets(all);
        setOutletId((held) => (held === '' ? (all[0]?.id ?? '') : held));
        if (all.length === 0) setState({ kind: 'loaded', agreements: [] });
      })
      .catch((error: unknown) => {
        if (live && !(error instanceof DOMException && error.name === 'AbortError')) {
          setState({ kind: 'failed', error });
        }
      });

    return () => {
      live = false;
      controller.abort();
    };
  }, [client]);

  useEffect(() => {
    if (outletId === '') return;

    const controller = new AbortController();
    let live = true;

    setState({ kind: 'loading' });

    collectPages<RevenueShareResource>((offset, limit) =>
      client.request(RevenueSharesController_listAt_v1, {
        path: { outletId },
        query: { offset, limit },
        signal: controller.signal,
      }),
    )
      .then((agreements) => {
        if (live) setState({ kind: 'loaded', agreements });
      })
      .catch((error: unknown) => {
        if (live && !(error instanceof DOMException && error.name === 'AbortError')) {
          setState({ kind: 'failed', error });
        }
      });

    return () => {
      live = false;
      controller.abort();
    };
  }, [client, outletId, reloads]);

  const outletItems = useMemo(
    () => outlets.map((outlet) => ({ label: outlet.name, value: outlet.id })),
    [outlets],
  );

  const outlet = outlets.find((candidate) => candidate.id === outletId);
  const agreements = state.kind === 'loaded' ? state.agreements : [];
  const forbidden = state.kind === 'failed' && isForbidden(state.error);
  const columns = mayWrite ? 5 : 4;

  return (
    <Screen
      title="Revenue shares"
      description="Each partner’s percentage of an outlet’s full collection, over the days it applies. A rate is never edited: close the current agreement, then open the next."
      action={
        mayWrite && outlet !== undefined ? (
          <Button onClick={() => setOpened({ kind: 'open' })}>
            <IconPlus data-icon="inline-start" />
            Open agreement
          </Button>
        ) : undefined
      }
      aside={
        <>
          <div className="space-y-2">
            <Label htmlFor="shares-outlet" className="text-sm text-muted-foreground">
              Outlet
            </Label>
            <Select
              value={outletId}
              items={outletItems}
              onValueChange={(next) => {
                if (next !== null) setOutletId(next);
              }}
            >
              <SelectTrigger
                id="shares-outlet"
                aria-label="Outlet"
                disabled={outlets.length === 0}
                className="h-10 w-full bg-card"
              >
                <SelectValue placeholder="Choose an outlet" />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  {outletItems.map((item) => (
                    <SelectItem key={item.value} value={item.value}>
                      {item.label}
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>
          </div>

          {state.kind === 'loaded' && outlet !== undefined && (
            <ScreenFact label="Agreements">
              <span className="font-mono numeric">{agreements.length}</span> at {outlet.name},
              closed ones included
            </ScreenFact>
          )}

          {!mayWrite && (
            <ScreenFact label="Read only">The owner opens and closes agreements.</ScreenFact>
          )}
        </>
      }
    >
      {notice !== null && (
        <Alert role="status">
          <IconCircleCheck />
          <AlertDescription>{notice}</AlertDescription>
        </Alert>
      )}

      <ScreenTable label="Revenue shares">
        <TableHeader>
          <TableRow>
            <TableHead>Partner</TableHead>
            <TableHead className="w-28 text-right">Rate</TableHead>
            <TableHead className="w-36">From</TableHead>
            <TableHead className="w-36">Last day</TableHead>
            {mayWrite && (
              <TableHead className="w-28">
                <span className="sr-only">Close</span>
              </TableHead>
            )}
          </TableRow>
        </TableHeader>

        {state.kind === 'loading' && <TableSkeleton columns={columns} />}

        {state.kind === 'failed' && (
          <TableNotice columns={columns}>
            {forbidden ? (
              <EmptyState
                title="This account cannot read revenue shares"
                description="Revenue shares are read by the owner and the manager. If that is what you are, sign out and back in — the role this browser is holding may be out of date."
              />
            ) : (
              <LoadFailed error={state.error} onRetry={reload} />
            )}
          </TableNotice>
        )}

        {state.kind === 'loaded' &&
          (agreements.length === 0 ? (
            <TableNotice columns={columns}>
              <EmptyState
                title={outlet === undefined ? 'No outlets yet' : `No agreements at ${outlet.name}`}
                description={
                  outlet === undefined
                    ? 'An agreement belongs to an outlet. Set one up under Outlets first.'
                    : mayWrite
                      ? 'Open agreement records a partner’s percentage of this outlet’s collection.'
                      : 'No partner is paid a share of this outlet’s collection. The owner records one.'
                }
              />
            </TableNotice>
          ) : (
            <TableBody>
              {/* In the API's order: by partner, newest first within each. */}
              {agreements.map((agreement) => (
                <TableRow key={agreement.id}>
                  <TableCell className="whitespace-normal">{agreement.vendor.name}</TableCell>
                  <TableCell className="text-right font-mono numeric">
                    {formatRate(agreement.rateMilliPercent)}
                  </TableCell>
                  <TableCell className="numeric">
                    {formatBusinessDate(agreement.effectiveFrom)}
                  </TableCell>
                  <TableCell className="numeric">
                    {agreement.effectiveTo === null ? (
                      <span className="text-muted-foreground">Open-ended</span>
                    ) : (
                      formatBusinessDate(agreement.effectiveTo)
                    )}
                  </TableCell>
                  {mayWrite && (
                    <TableCell>
                      {/* An end is set once, so only an open-ended one offers it. */}
                      {agreement.effectiveTo === null && (
                        <Button
                          variant="outline"
                          size="sm"
                          aria-label={`Close the agreement with ${agreement.vendor.name}`}
                          onClick={() => setOpened({ kind: 'close', agreement })}
                        >
                          Close
                        </Button>
                      )}
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          ))}
      </ScreenTable>

      {opened?.kind === 'open' && outlet !== undefined && (
        <OpenAgreementDialog
          outletId={outlet.id}
          outletName={outlet.name}
          agreements={agreements}
          onClose={() => setOpened(null)}
          onCloseFirst={(agreement) => setOpened({ kind: 'close', agreement })}
          onOpened={(agreement) => {
            setOpened(null);
            setNotice(
              `${agreement.vendor.name} is paid ${formatRate(agreement.rateMilliPercent)} of ${outlet.name}’s collection from ${formatBusinessDate(agreement.effectiveFrom)}.`,
            );
            reload();
          }}
        />
      )}

      {opened?.kind === 'close' && (
        <CloseAgreementDialog
          agreement={opened.agreement}
          onClose={() => setOpened(null)}
          onStale={reload}
          onClosed={(agreement) => {
            setOpened(null);
            setNotice(
              `The agreement with ${agreement.vendor.name} ends on ${formatBusinessDate(agreement.effectiveTo ?? agreement.effectiveFrom)}.`,
            );
            reload();
          }}
        />
      )}
    </Screen>
  );
}
